import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle2, Loader2, BadgePoundSterling } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const QuoteTool = () => {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", job_description: "", preferred_date: "" });

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.address || !form.job_description) {
      toast.error("Please fill in your details and describe the job.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) { toast.error("Please enter a valid email address."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      toast.success("Request sent! We'll be in touch shortly.");
      setForm({ name: "", phone: "", email: "", address: "", job_description: "", preferred_date: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full border-2 border-[#0A1A4A] px-4 py-3 outline-none focus:border-[#2563EB] text-[#0A1A4A] placeholder:text-[#0A1A4A]/40 bg-white";

  return (
    <section id="quote" className="bg-[#0A1A4A] py-20 sm:py-28 relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-10 items-start">
        {/* Left copy */}
        <div className="lg:col-span-2">
          <div className="font-mono-accent text-xs tracking-widest uppercase text-[#EAB308] mb-3">/ Get Started</div>
          <h2 className="font-display font-black text-white text-4xl sm:text-6xl tracking-tight leading-[0.95]">
            TELL US THE JOB.<br /><span className="text-[#2563EB]">WE'LL SORT IT.</span>
          </h2>
          <p className="text-white/60 mt-5 text-base max-w-md">
            Fill in the quick form and describe what needs doing. We'll get back to you with a price and a date.
          </p>

          <div className="mt-8 flex items-start gap-4 bg-white/5 border-2 border-white/15 px-5 py-4">
            <BadgePoundSterling size={34} className="text-[#EAB308] shrink-0" />
            <div>
              <div className="font-display font-black text-white text-xl leading-tight">Minimum charge of £60<span className="text-[#EAB308]"> per job</span></div>
              <div className="text-white/60 text-sm mt-1.5">Every job has a minimum charge of £60. Your final price is <strong className="text-white/90">quoted to you by email</strong> once we've reviewed the details.</div>
            </div>
          </div>
        </div>

        {/* Form card */}
        <div className="lg:col-span-3 bg-white border-2 border-white p-6 sm:p-8 shadow-hard-blue">
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-12" data-testid="booking-success">
                <CheckCircle2 size={56} className="text-[#2563EB] mx-auto mb-5" />
                <h3 className="font-display font-black text-2xl uppercase text-[#0A1A4A]">Request Sent!</h3>
                <p className="text-[#0A1A4A]/70 mt-3 text-sm max-w-sm mx-auto">Thanks — we've got your job details. We'll email you your <strong>final price</strong> (minimum charge £60 per job) and confirm a date shortly.</p>
                <button onClick={() => setDone(false)} className="mt-6 font-mono-accent text-xs tracking-widest uppercase text-[#2563EB] underline" data-testid="booking-another">
                  Send another request
                </button>
              </motion.div>
            ) : (
              <motion.form key="form" onSubmit={submit} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                <div>
                  <label className="font-mono-accent text-[11px] tracking-widest uppercase text-[#0A1A4A]/60">Your name</label>
                  <input value={form.name} onChange={setField("name")} placeholder="Full name" data-testid="booking-name" className={`${inputCls} mt-1.5`} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono-accent text-[11px] tracking-widest uppercase text-[#0A1A4A]/60">Phone</label>
                    <input value={form.phone} onChange={setField("phone")} placeholder="07..." data-testid="booking-phone" className={`${inputCls} mt-1.5`} />
                  </div>
                  <div>
                    <label className="font-mono-accent text-[11px] tracking-widest uppercase text-[#0A1A4A]/60">Email</label>
                    <input value={form.email} onChange={setField("email")} placeholder="you@email.com" data-testid="booking-email" className={`${inputCls} mt-1.5`} />
                  </div>
                </div>
                <div>
                  <label className="font-mono-accent text-[11px] tracking-widest uppercase text-[#0A1A4A]/60">Address / postcode</label>
                  <input value={form.address} onChange={setField("address")} placeholder="Swansea, SA1..." data-testid="booking-address" className={`${inputCls} mt-1.5`} />
                </div>
                <div>
                  <label className="font-mono-accent text-[11px] tracking-widest uppercase text-[#0A1A4A]/60">Describe the job</label>
                  <textarea value={form.job_description} onChange={setField("job_description")} rows={4} placeholder="e.g. Need a 50 inch TV wall-mounted in the living room, and a leaky kitchen tap fixed." data-testid="booking-job-description" className={`${inputCls} mt-1.5 resize-none`} />
                </div>
                <div>
                  <label className="font-mono-accent text-[11px] tracking-widest uppercase text-[#0A1A4A]/60">Preferred date (optional)</label>
                  <input type="date" value={form.preferred_date} onChange={setField("preferred_date")} data-testid="booking-date" className={`${inputCls} mt-1.5`} />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="booking-submit"
                  className="w-full flex items-center justify-center gap-2 bg-[#2563EB] text-white font-display font-extrabold uppercase tracking-wide py-4 border-2 border-[#0A1A4A] shadow-hard hover:bg-[#0A1A4A] transition-colors disabled:opacity-60"
                >
                  {submitting ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <><Send size={18} /> Send My Request</>}
                </button>
                <p className="text-center text-xs text-[#0A1A4A]/50 pt-1" data-testid="pricing-note">
                  <strong className="text-[#0A1A4A]">Minimum charge of £60 per job.</strong> Final price quoted by email.
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
